import apiClient from '../config/api';
import { syncQueueDB, receiptsDB } from '../db/indexedDB';

const canSendNetworkTraffic = () => {
  if (typeof window === 'undefined') {
    return true;
  }
  
  if (window.__wealthbag_tokenRefreshing) {
    return false;
  }
  
  if (window.__wealthbag_tokenRefreshFailed) {
    return false;
  }
  
  return true;
};

class SyncEngine {
  constructor() {
    // HMR Protection: Check if event listeners are already registered globally
    const LISTENER_KEY = '__wealthbag_sync_listeners_registered';
    const INSTANCE_ID = `sync-engine-${Date.now()}`;
    
    this.isSyncing = false;
    this.syncInterval = null;
    this.onlineStatus = navigator.onLine;
    this.statusCheckInterval = null;
    // Track receipts currently being synced to prevent duplicates
    this.syncingReceiptIds = new Set();
    // Track sync queue operations currently being synced
    this.syncingQueueIds = new Set();
    // Track pending API requests by payload hash to prevent duplicate API calls
    this.pendingApiRequests = new Map();
    // Shared lock using localStorage to prevent concurrent bulk syncs across all instances
    this.BULK_SYNC_LOCK_KEY = 'wealthbag_bulk_sync_lock';
    this.BULK_SYNC_LOCK_TIMEOUT = 30000; // 30 seconds timeout
    
    // Debounce mechanism to prevent multiple syncs when coming back online
    this.lastSyncTriggerTime = 0;
    this.lastSyncCompletionTime = 0; // Track when sync actually completed
    this.onlineSyncDebounceTimeout = null;
    this.SYNC_DEBOUNCE_MS = 5000; // Don't trigger sync more than once every 5 seconds
    this.lastOnlineEventTime = 0;

    // HMR Protection: Only register listeners once globally
    if (!window[LISTENER_KEY]) {
      window[LISTENER_KEY] = true;
      
      // Store handlers on window so they can be removed later
      window.__wealthbag_onlineHandler = () => {
        const now = Date.now();
        
        // Update all instances' online status
        if (window.__wealthbag_syncEngine) {
          window.__wealthbag_syncEngine.onlineStatus = true;
          window.__wealthbag_syncEngine.lastOnlineEventTime = now;
          
          // Clear any existing debounce timeout
          if (window.__wealthbag_syncEngine.onlineSyncDebounceTimeout) {
            clearTimeout(window.__wealthbag_syncEngine.onlineSyncDebounceTimeout);
            window.__wealthbag_syncEngine.onlineSyncDebounceTimeout = null;
          }
          
          // Debounce: Only trigger sync if enough time has passed since last sync completion
          const timeSinceLastCompletion = now - window.__wealthbag_syncEngine.lastSyncCompletionTime;
          if (timeSinceLastCompletion < window.__wealthbag_syncEngine.SYNC_DEBOUNCE_MS && window.__wealthbag_syncEngine.lastSyncCompletionTime > 0) {
            // Schedule sync after debounce period
            window.__wealthbag_syncEngine.onlineSyncDebounceTimeout = setTimeout(() => {
              window.__wealthbag_syncEngine.onlineSyncDebounceTimeout = null;
              if (window.__wealthbag_syncEngine) {
                window.__wealthbag_syncEngine.triggerSync('online-event');
              }
            }, window.__wealthbag_syncEngine.SYNC_DEBOUNCE_MS - timeSinceLastCompletion);
          } else {
            // Add a small delay to ensure network is fully available
            window.__wealthbag_syncEngine.onlineSyncDebounceTimeout = setTimeout(() => {
              window.__wealthbag_syncEngine.onlineSyncDebounceTimeout = null;
              if (window.__wealthbag_syncEngine) {
                window.__wealthbag_syncEngine.triggerSync('online-event');
              }
            }, 1000);
          }
        }
      };
      
      window.__wealthbag_offlineHandler = () => {
        // Update all instances' online status
        if (window.__wealthbag_syncEngine) {
          window.__wealthbag_syncEngine.onlineStatus = false;
          
          // Clear any pending online sync
          if (window.__wealthbag_syncEngine.onlineSyncDebounceTimeout) {
            clearTimeout(window.__wealthbag_syncEngine.onlineSyncDebounceTimeout);
            window.__wealthbag_syncEngine.onlineSyncDebounceTimeout = null;
          }
          
          window.__wealthbag_syncEngine.stopSync();
        }
      };
      
      // Register listeners once globally
      window.addEventListener('online', window.__wealthbag_onlineHandler);
      window.addEventListener('offline', window.__wealthbag_offlineHandler);
    }
    
    // Store this instance globally (will be overwritten on HMR, but that's ok - we use the latest)
    window.__wealthbag_syncEngine = this;
    
    // Periodically check online status (in case events don't fire)
    // HMR Protection: Only create one interval globally
    const STATUS_CHECK_KEY = '__wealthbag_status_check_interval';
    if (!window[STATUS_CHECK_KEY]) {
      window[STATUS_CHECK_KEY] = setInterval(() => {
        // Use the latest instance
        if (window.__wealthbag_syncEngine) {
          const engine = window.__wealthbag_syncEngine;
          const wasOffline = !engine.onlineStatus;
          engine.onlineStatus = navigator.onLine;
          
          // If we just came back online, start syncing (but respect debounce)
          if (wasOffline && engine.onlineStatus) {
            const now = Date.now();
            const timeSinceLastCompletion = now - engine.lastSyncCompletionTime;
            const timeSinceLastOnlineEvent = now - engine.lastOnlineEventTime;
            
            // Only trigger if:
            // 1. Enough time has passed since last sync completion
            // 2. The online event didn't just fire (within 2 seconds) - let the event handler deal with it
            if (timeSinceLastCompletion >= engine.SYNC_DEBOUNCE_MS && timeSinceLastOnlineEvent > 2000) {
              setTimeout(() => {
                if (window.__wealthbag_syncEngine) {
                  window.__wealthbag_syncEngine.triggerSync('periodic-check');
                }
              }, 1000);
            }
          }
        }
      }, 5000); // Check every 5 seconds
    }
    
    // Fix any existing receipts with undefined synced field
    this.initializeReceipts();
    
    // Start sync if online
    if (this.onlineStatus) {
      this.startSync();
    }
  }
  
  // Centralized method to trigger sync with logging
  triggerSync(source) {
    const now = Date.now();
    const timeSinceLastCompletion = now - this.lastSyncCompletionTime;
    const timeSinceLastTrigger = now - this.lastSyncTriggerTime;

    if (!canSendNetworkTraffic()) {
      return;
    }
    
    // DEBOUNCE CHECK: Don't trigger if sync completed recently
    if (timeSinceLastCompletion < this.SYNC_DEBOUNCE_MS && this.lastSyncCompletionTime > 0) {
      return;
    }
    
    // Check if already syncing
    if (this.isSyncing) {
      return;
    }
    
    // Check shared lock
    const syncLockKey = 'wealthbag_sync_lock';
    const existingSyncLock = localStorage.getItem(syncLockKey);
    if (existingSyncLock) {
      const lockTime = parseInt(existingSyncLock, 10);
      const lockAge = now - lockTime;
      if (lockAge < 10000) {
        return;
      } else {
        localStorage.removeItem(syncLockKey);
      }
    }
    
    // Update last trigger time (only if we're actually going to sync)
    this.lastSyncTriggerTime = now;
    
    // Start sync interval if not already running
    if (!this.syncInterval) {
      this.startSync();
    } else {
      // Just trigger immediate sync
      this.sync();
    }
  }
  
  // Initialize and fix receipt records
  async initializeReceipts() {
    try {
      await receiptsDB.fixSyncedField();
    } catch (error) {
      // Error handled silently
    }
  }
  
  // Start automatic sync
  startSync() {
    // HMR Protection: Use global flag to prevent multiple intervals across instances
    const SYNC_INTERVAL_KEY = '__wealthbag_sync_interval';
    
    // Check if interval already exists globally
    if (window[SYNC_INTERVAL_KEY]) {
      // Update our local reference
      this.syncInterval = window[SYNC_INTERVAL_KEY];
      return;
    }
    
    // Prevent multiple intervals on this instance
    if (this.syncInterval) {
      return;
    }

    // Sync immediately (but check debounce and locks)
    if (!this.isSyncing) {
      const now = Date.now();
      const timeSinceLastCompletion = now - this.lastSyncCompletionTime;
      
      // Check debounce
      if (timeSinceLastCompletion < this.SYNC_DEBOUNCE_MS && this.lastSyncCompletionTime > 0) {
        // Schedule sync after debounce period
        setTimeout(() => {
          if (window.__wealthbag_syncEngine && !window.__wealthbag_syncEngine.isSyncing) {
            window.__wealthbag_syncEngine.sync();
          }
        }, this.SYNC_DEBOUNCE_MS - timeSinceLastCompletion);
      } else {
        this.sync();
      }
    }
    
    // Then sync every 30 seconds - store globally to prevent duplicates
    this.syncInterval = setInterval(() => {
      // Use the latest instance
      const engine = window.__wealthbag_syncEngine;
      if (!engine) return;
      
      // Only sync if not already syncing and debounce period has passed
      if (!engine.isSyncing) {
        const now = Date.now();
        const timeSinceLastCompletion = now - engine.lastSyncCompletionTime;
        
        if (timeSinceLastCompletion >= engine.SYNC_DEBOUNCE_MS || engine.lastSyncCompletionTime === 0) {
          engine.sync();
        }
      }
    }, 30000);
    
    // Store globally
    window[SYNC_INTERVAL_KEY] = this.syncInterval;
  }
  
  // Stop automatic sync
  stopSync() {
    const SYNC_INTERVAL_KEY = '__wealthbag_sync_interval';
    
    if (this.syncInterval) {
      clearInterval(this.syncInterval);
      this.syncInterval = null;
    }
    
    // Also clear global reference if it matches
    if (window[SYNC_INTERVAL_KEY] === this.syncInterval) {
      window[SYNC_INTERVAL_KEY] = null;
    }
  }
  
  // Cleanup (for testing or if needed)
  destroy() {
    this.stopSync();
    if (this.statusCheckInterval) {
      clearInterval(this.statusCheckInterval);
      this.statusCheckInterval = null;
    }
  }
  
  // Check if actually online (navigator.onLine can be unreliable)
  async checkNetworkConnectivity() {
    if (!navigator.onLine) {
      return false;
    }
    
    try {
      // Try to fetch a small resource to verify connectivity
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 3000);
      
      const response = await fetch('/favicon.ico', { 
        method: 'HEAD',
        cache: 'no-cache',
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      return response.ok;
    } catch (error) {

      return false;
    }
  }

  // Main sync function
  async sync() {
    if (!canSendNetworkTraffic()) {
      return;
    }
    
    const syncStartTime = Date.now();
    const syncId = `sync-${syncStartTime}`;
    
    // DEBOUNCE CHECK: Don't start sync if one completed recently
    const timeSinceLastCompletion = syncStartTime - this.lastSyncCompletionTime;
    if (timeSinceLastCompletion < this.SYNC_DEBOUNCE_MS && this.lastSyncCompletionTime > 0) {
      return;
    }
    
    // Shared lock key for cross-instance sync prevention
    const syncLockKey = 'wealthbag_sync_lock';
    
    // STRICT CHECK: Prevent concurrent syncs (both instance-level and shared)
    if (this.isSyncing) {
      return;
    }
    
    // Also check shared lock to prevent syncs across HMR instances
    const existingSyncLock = localStorage.getItem(syncLockKey);
    if (existingSyncLock) {
      const lockTime = parseInt(existingSyncLock, 10);
      const now = Date.now();
      const lockAge = now - lockTime;
      
      // If lock is still valid (not expired), skip
      if (lockAge < 10000) { // 10 second timeout for regular sync
        return;
      } else {
        // Lock expired, remove it
        localStorage.removeItem(syncLockKey);
      }
    }
    
    // Update online status from navigator
    this.onlineStatus = navigator.onLine;

    if (!this.onlineStatus) {
      return;
    }
    
    // Verify network connectivity
    const isConnected = await this.checkNetworkConnectivity();
    if (!isConnected) {
      this.onlineStatus = false;
      return;
    }

    // Set syncing flag IMMEDIATELY to prevent concurrent syncs
    this.isSyncing = true;
    localStorage.setItem(syncLockKey, Date.now().toString());

    try {
      // Clear any stuck in-memory tracking (safety measure)
      this.syncingReceiptIds.clear();
      this.syncingQueueIds.clear();
      this.pendingApiRequests.clear();

      // Sync pending receipts first
      await this.syncPendingReceipts();

      // Sync pending queue operations

      await this.syncPendingQueue();

      const syncDuration = Date.now() - syncStartTime;
    } catch (error) {
      const syncDuration = Date.now() - syncStartTime;
      // Don't re-throw - we want to continue even if one part fails
    } finally {
      // Clear in-memory tracking
      this.syncingReceiptIds.clear();
      this.syncingQueueIds.clear();
      // Don't clear pendingApiRequests here - let them complete naturally
      
      this.isSyncing = false;
      localStorage.removeItem(syncLockKey);
      const syncDuration = Date.now() - syncStartTime;
      this.lastSyncCompletionTime = Date.now(); // Track when sync actually completed
    }
  }
  
  // Clean up receipts stuck in "syncing" status (e.g., from interrupted syncs)
  async cleanupStuckSyncingReceipts() {
    try {
      const allReceipts = await receiptsDB.getAll();
      const db = (await import('../db/indexedDB')).default;
      const stuckReceipts = allReceipts.filter(r => r.sync_status === 'syncing' && r.synced === false);
      
      if (stuckReceipts.length > 0) {
        for (const receipt of stuckReceipts) {
          await db.receipts.update(receipt.id, {
            sync_status: 'pending',
          });
        }
      }
    } catch (error) {
      // Error handled silently
    }
  }

  // Fix receipts missing sync_id (for old receipts created before sync_id was implemented)
  async fixMissingSyncIds() {
    try {
      const allReceipts = await receiptsDB.getAll();
      const db = (await import('../db/indexedDB')).default;
      let fixedCount = 0;
      
      for (const receipt of allReceipts) {
        if (!receipt.sync_id && !receipt.synced) {
          // Generate sync_id for old receipts that don't have it
          const syncId = `sync-${Date.now()}-${Math.random().toString(36).substring(2, 11)}-${receipt.id}`;
          await db.receipts.update(receipt.id, { sync_id: syncId });
          fixedCount++;
          console.log(`[SYNC] 🔧 Fixed missing sync_id for receipt ${receipt.id}: ${syncId}`);
        }
      }
      
      if (fixedCount > 0) {
        console.log(`[SYNC] ✅ Fixed ${fixedCount} receipts missing sync_id`);
      }
    } catch (error) {
      console.error('[SYNC] ❌ Error fixing missing sync_ids:', error);
    }
  }

  // Sync pending receipts using bulk endpoint
  async syncPendingReceipts() {
    const startTime = Date.now();
    
    // First, clean up any receipts stuck in "syncing" status
    await this.cleanupStuckSyncingReceipts();
    
    // Fix any receipts missing sync_id (for old receipts)
    await this.fixMissingSyncIds();
    
    let unsyncedReceipts = [];
    
    try {
      unsyncedReceipts = await receiptsDB.getUnsynced();
    } catch (error) {
      // Try to fix any records with undefined synced field
      try {
        const allReceipts = await receiptsDB.getAll();
        const db = (await import('../db/indexedDB')).default;
        let fixedCount = 0;
        for (const receipt of allReceipts) {
          if (receipt.synced === undefined || receipt.synced === null) {
            await db.receipts.update(receipt.id, { synced: false });
            fixedCount++;
          }
        }
        // Retry getting unsynced receipts
        unsyncedReceipts = await receiptsDB.getUnsynced();
      } catch (fixError) {
        return;
      }
    }
    
    if (unsyncedReceipts.length === 0) {

      return;
    }
    
    // Prepare all receipts for bulk sync
    const salesToSync = [];
    const receiptIdMap = new Map(); // Map to track which receipt ID corresponds to which sale index
    
    // Process all receipts and prepare for bulk sync
    for (let i = 0; i < unsyncedReceipts.length; i++) {
      const receipt = unsyncedReceipts[i];
      const receiptId = receipt.id;
      
      // STRICT CHECK 1: Skip if already in our in-memory syncing set
      if (this.syncingReceiptIds.has(receiptId)) {
        continue;
      }
      
      // STRICT CHECK 2: Re-check if receipt is still unsynced
      const currentReceipt = await receiptsDB.getById(receiptId);
      if (!currentReceipt) {
        continue;
      }
      
      if (currentReceipt.synced === true) {
        continue;
      }
      
      if (currentReceipt.sync_status === 'syncing') {
        continue;
      }
      
      // CRITICAL CHECK: Verify sync_id exists (must be generated at storage time in Checkout.js)
      if (!currentReceipt.sync_id) {
        console.error(`[SYNC] ❌ Receipt ${receiptId} is missing sync_id! Receipt data:`, {
          id: receiptId,
          branch_id: currentReceipt.branch_id,
          items_count: currentReceipt.items?.length,
          hasSyncId: !!currentReceipt.sync_id,
          receiptKeys: Object.keys(currentReceipt)
        });
        // Skip this receipt - it should have sync_id from storage
        // Remove from syncing set
        this.syncingReceiptIds.delete(receiptId);
        continue;
      }
      
      // Validate receipt has required fields
      if (!currentReceipt.branch_id) {
        console.warn(`[SYNC] ⚠️ Receipt ${receiptId} missing branch_id - skipping`);
        this.syncingReceiptIds.delete(receiptId);
        continue;
      }
      
      if (!currentReceipt.items || !Array.isArray(currentReceipt.items) || currentReceipt.items.length === 0) {
        console.warn(`[SYNC] ⚠️ Receipt ${receiptId} missing items - skipping`);
        this.syncingReceiptIds.delete(receiptId);
        continue;
      }
      
      console.log(`[SYNC] 📦 Processing receipt ${receiptId} with sync_id: ${currentReceipt.sync_id}`);
      
      // Add to in-memory set
      this.syncingReceiptIds.add(receiptId);
      
      // Mark as syncing in database
      const db = (await import('../db/indexedDB')).default;
      await db.receipts.update(receiptId, {
        sync_status: 'syncing',
      });
      
      // Process items - aggregate by product_id to prevent duplicates
      // Use currentReceipt (fresh from DB) instead of receipt (from getUnsynced)
      const itemsMap = new Map();
      const isZigPayment = currentReceipt?.payment_method && currentReceipt.payment_method.toLowerCase().includes('zig');
      const exchangeRate = currentReceipt?.exchange_rate_used || currentReceipt?.exchangeRate || null;
      for (const item of currentReceipt.items) {
        const productId = item.product_id ?? item.id;
        if (!productId) {

          continue;
        }
        
        const productIdNum = Number(productId);
        const quantity = Number(item.quantity) || 0;

        // Derive unit_price, converting to ZIG when required so backend receives ZIG values
        let unitPrice = null;
        if (item.unit_price !== undefined && item.unit_price !== null && !Number.isNaN(Number(item.unit_price))) {
          unitPrice = Number(item.unit_price);
        } else if (item.price !== undefined && item.price !== null && !Number.isNaN(Number(item.price))) {
          unitPrice = Number(item.price);
        }

        const itemCurrency = (item.currency || currentReceipt.currency || '').toString().toUpperCase();
        const itemIsZigCurrency = itemCurrency.includes('ZIG');

        if (isZigPayment && !itemIsZigCurrency && exchangeRate && unitPrice !== null) {
          unitPrice = Number((unitPrice * exchangeRate).toFixed(2));
        }
        
        if (itemsMap.has(productIdNum)) {
          const existing = itemsMap.get(productIdNum);

          itemsMap.set(productIdNum, {
            quantity: existing.quantity + quantity,
            unit_price: existing.unit_price ?? unitPrice,
          });
        } else {
          itemsMap.set(productIdNum, { quantity, unit_price: unitPrice });
        }
      }
      
      // Convert map to array format
      const itemsArray = Array.from(itemsMap.entries()).map(([product_id, data]) => {
        const payloadItem = {
          product_id: product_id,
          quantity: String(data.quantity),
        };

        if (data.unit_price !== null && data.unit_price !== undefined && !Number.isNaN(Number(data.unit_price))) {
          payloadItem.unit_price = data.unit_price;
        }

        return payloadItem;
      });
      
      // Use currentReceipt's sync_id from IndexedDB (generated when stored in Checkout.js)
      // We already verified sync_id exists above, so use it directly
      const receiptSyncId = currentReceipt.sync_id;
      
      // Add to bulk sync array
      const salePayload = {
        branch_id: Number(currentReceipt.branch_id),
        type_of_payment: currentReceipt.payment_method || 'cash',
        notes: `Offline batch #${i + 1} - Receipt ${currentReceipt.receipt_number || receiptId}`,
        items: itemsArray,
        sync_id: receiptSyncId, // sync_id from IndexedDB (generated at storage time in Checkout.js)
      };

      // Add discount_code if present (backend will recalculate discount based on code)
      const discountCode = currentReceipt.discount_code;
      if (discountCode && typeof discountCode === 'string' && discountCode.trim()) {
        salePayload.discount_code = discountCode.trim();
        console.log(`[SYNC] 💰 Added discount_code "${discountCode}" to sale ${receiptId}`);
      } else {
        console.log(`[SYNC] ℹ️ No discount_code for sale ${receiptId} (value: ${discountCode})`);
      }

      salesToSync.push(salePayload);
      
      console.log(`[SYNC] ✅ Added receipt ${receiptId} to bulk sync with sync_id: ${receiptSyncId}`, {
        hasDiscountCode: !!salePayload.discount_code,
        discountCode: salePayload.discount_code || 'none',
        itemsCount: itemsArray.length
      });
      
      // Map sale index to receipt ID for later processing
      receiptIdMap.set(salesToSync.length - 1, receiptId);
    }
    
    // If no valid receipts to sync, return
    if (salesToSync.length === 0) {

      // Clear syncing flags
      for (const receiptId of Array.from(this.syncingReceiptIds)) {
        if (receiptIdMap.has(Array.from(receiptIdMap.values()).indexOf(receiptId))) {
          this.syncingReceiptIds.delete(receiptId);
        }
      }
      const duration = Date.now() - startTime;
      return;
    }
    
    // CRITICAL: Prevent concurrent bulk syncs using shared lock (works across HMR instances)
    const lockKey = this.BULK_SYNC_LOCK_KEY;
    const lockTimeout = this.BULK_SYNC_LOCK_TIMEOUT;
    
    // Check if bulk sync is already in progress (check shared lock)
    const existingLock = localStorage.getItem(lockKey);
    if (existingLock) {
      const lockTime = parseInt(existingLock, 10);
      const now = Date.now();
      const lockAge = now - lockTime;
      
      // If lock is still valid (not expired), skip
      if (lockAge < lockTimeout) {
        // Reset syncing flags for these receipts
        for (const [, receiptId] of receiptIdMap.entries()) {
          this.syncingReceiptIds.delete(receiptId);
          const db = (await import('../db/indexedDB')).default;
          await db.receipts.update(receiptId, {
            sync_status: 'pending',
          });
        }
        const duration = Date.now() - startTime;
        return;
      } else {
        // Lock expired, remove it
        localStorage.removeItem(lockKey);
      }
    }
    
    // Set shared bulk sync lock
    localStorage.setItem(lockKey, Date.now().toString());
    
    // Verify ALL sales have sync_id from IndexedDB (should never be missing if stored correctly)
    const salesWithoutSyncId = salesToSync.filter(s => !s.sync_id);
    if (salesWithoutSyncId.length > 0) {
      console.error('[SYNC] ❌ ERROR: Some sales are missing sync_id from IndexedDB!', salesWithoutSyncId);
      throw new Error(`Cannot sync: ${salesWithoutSyncId.length} sales are missing sync_id. Receipts must have sync_id generated at storage time.`);
    }
    
    // Log sync_id verification
    const allSyncIds = salesToSync.map(s => s.sync_id);
    console.log('[SYNC] ✅ Bulk sync verification:', { 
      salesCount: salesToSync.length, 
      syncIdsCount: allSyncIds.length,
      firstSale: salesToSync[0], 
      allSyncIds: allSyncIds,
      allSalesHaveSyncId: salesToSync.every(s => s.sync_id)
    });
    
    // Store sync IDs in localStorage to track recent syncs (for duplicate prevention)
    const RECENT_SYNC_IDS_KEY = 'wealthbag_recent_sync_ids';
    const recentSyncIds = JSON.parse(localStorage.getItem(RECENT_SYNC_IDS_KEY) || '[]');
    // Keep only last 100 sync IDs (prevent localStorage bloat)
    if (recentSyncIds.length > 100) {
      recentSyncIds.splice(0, recentSyncIds.length - 100);
    }
    // Add all unique sync IDs from this bulk sync
    allSyncIds.forEach(syncId => {
      if (syncId && !recentSyncIds.find(r => r.syncId === syncId)) {
        recentSyncIds.push({ syncId, timestamp: Date.now() });
      }
    });
    localStorage.setItem(RECENT_SYNC_IDS_KEY, JSON.stringify(recentSyncIds));
    
    // Make bulk API call
    try {
      const bulkPayload = {
        sales: salesToSync, // Each sale contains sync_id
      };
      
      // Log payload structure to verify sync_id is present
      console.log('Bulk sync payload structure:', {
        salesCount: bulkPayload.sales.length,
        sampleSale: bulkPayload.sales[0],
        allHaveSyncId: bulkPayload.sales.every(s => s.sync_id),
        syncIds: bulkPayload.sales.map(s => s.sync_id)
      });
      
      const response = await apiClient.post('/api/sales/bulk/', bulkPayload);
      console.log('Bulk payload sent successfully:', {
        salesCount: bulkPayload.sales.length,
        responseStatus: response.status
      });
       // Check if server indicates this sync ID was already processed (duplicate)
       if (response.data?.duplicate === true || response.data?.already_processed === true || response.data?.sync_id_processed === true) {
         // Still mark receipts as synced since server already processed them
       }
      
      // Process response and mark receipts as synced IMMEDIATELY
      // Handle different possible response formats
      const syncPromises = [];
      
      if (response.data) {
        let salesArray = null;
        
        // Check if response.data is an array of sale objects
        if (Array.isArray(response.data)) {
          salesArray = response.data;
        } 
        // Check if response.data has a 'created' array (bulk endpoint format)
        else if (response.data.created && Array.isArray(response.data.created)) {
          salesArray = response.data.created;
        }
        // Check if response.data has a 'sales' array
        else if (response.data.sales && Array.isArray(response.data.sales)) {
          salesArray = response.data.sales;
        }
        // Check if response.data has a 'results' array (common REST pattern)
        else if (response.data.results && Array.isArray(response.data.results)) {
          salesArray = response.data.results;
        }
        
        if (salesArray) {
          for (let i = 0; i < salesArray.length && i < salesToSync.length; i++) {
            const receiptId = receiptIdMap.get(i);
            if (receiptId) {
              const saleResponse = salesArray[i];
              const serverReceiptId = saleResponse?.id || saleResponse?.receipt_id || saleResponse?.sale_id || saleResponse;
              
              // Mark as synced IMMEDIATELY (don't await, do in parallel)
              syncPromises.push(
                receiptsDB.markSynced(receiptId, serverReceiptId).then(() => {
                  this.syncingReceiptIds.delete(receiptId);
                })
              );
            }
          }
        } else {
          // If response format is unexpected, mark as synced with fallback IDs
          for (const [index, receiptId] of receiptIdMap.entries()) {
            const currentIndex = index; // Capture index for closure
            syncPromises.push(
              receiptsDB.markSynced(receiptId, `bulk-${Date.now()}-${currentIndex}`).then(() => {
                this.syncingReceiptIds.delete(receiptId);
              })
            );
          }
        }
      }
      
      // Wait for all sync operations to complete
      await Promise.all(syncPromises);
      const syncedCount = syncPromises.length;
      const duration = Date.now() - startTime;
    } catch (error) {
      const duration = Date.now() - startTime;
      
      // Reset all receipts to pending status for retry
      const db = (await import('../db/indexedDB')).default;
      for (const [, receiptId] of receiptIdMap.entries()) {
        this.syncingReceiptIds.delete(receiptId);
        
        const receipt = await receiptsDB.getById(receiptId);
        const retryCount = receipt?.retry_count || 0;
        
        await db.receipts.update(receiptId, {
          sync_status: 'pending',
          retry_count: retryCount + 1,
          sync_error: error.message || error.toString(),
        });
      }

      } finally {
      // Always release the shared bulk sync lock
      localStorage.removeItem(lockKey);

    }
  }
  

  // Sync pending queue operations (non-sales endpoints only)
  // Note: Sales are handled via receiptsDB and synced using bulk endpoint
  
  // Create a unique hash for API requests to prevent duplicates
  createRequestHash(endpoint, payload) {
    // Create a deterministic hash from endpoint and payload
    const normalizedPayload = {
      branch_id: payload.branch_id,
      type_of_payment: payload.type_of_payment,
      items: payload.items
        .map(item => ({
          product_id: item.product_id,
          quantity: item.quantity,
          unit_price: item.unit_price ?? null,
        }))
        .sort((a, b) => a.product_id - b.product_id)
    };
    return `${endpoint}:${JSON.stringify(normalizedPayload)}`;
  }

  // Force sync now
  async forceSync() {
    // Update online status before forcing sync
    this.onlineStatus = navigator.onLine;

    // Use triggerSync to respect debounce and locks
    this.triggerSync('forceSync');
    
    // Wait for sync to complete if it started
    if (!this.isSyncing) {
      // If sync didn't start (was already in progress or debounced), wait a bit
    }
  }
  
  // Get sync status
  getStatus() {
    return {
      isOnline: this.onlineStatus,
      isSyncing: this.isSyncing,
    };
  }
}

// Create singleton instance
const syncEngine = new SyncEngine();

export default syncEngine;

